/**
 * Templates section for the Test Suite Configuration page.
 *
 * Displays an expandable section with template pattern cards grouped by
 * tool. Each template card shows the id, template string (editable),
 * description, weight (editable with 0–1 range validation), and an
 * isComparison indicator. Templates can be toggled on/off, duplicated,
 * removed, or added as custom entries.
 *
 * Comparison templates expose editable `parameters.comparison` blocks,
 * and templates with implicit parameters expose editable
 * `parameters.implicit` blocks.
 *
 * @see Requirements 8.1–8.8
 */

import React, { useMemo, useState } from 'react';
import {
  ExpandableSection,
  SpaceBetween,
  FormField,
  Input,
  Textarea,
  Toggle,
  Button,
  Header,
  Container,
  ColumnLayout,
  Box,
  Badge,
  Alert,
  TokenGroup,
} from '@amzn/awsui-components-react';
import {
  ConfigPageAction,
  ConfigPageState,
  TemplateDefinition,
  ValidationErrors,
} from './types';
import { validateUniqueIds } from './configGeneratorValidators';

// ─── Constants ─────────────────────────────────────────────────────────

const WEIGHT_MIN = 0;
const WEIGHT_MAX = 1;
const TEMPLATE_STRING_ROWS = 2;

// ─── Props ─────────────────────────────────────────────────────────────

export interface TemplatesSectionProps {
  state: ConfigPageState;
  dispatch: React.Dispatch<ConfigPageAction>;
  validationErrors: ValidationErrors;
}

// ─── Helper: Create an empty custom template ───────────────────────────

function createEmptyTemplate(): TemplateDefinition & { enabled: boolean } {
  return {
    id: `custom_${Date.now()}`,
    template: '',
    description: '',
    parameters: {
      required: [],
    },
    weight: 0.1,
    isComparison: false,
    enabled: true,
  };
}

// ─── Sub-component: Placeholder Input ──────────────────────────────────

interface PlaceholderInputProps {
  onAdd: (value: string) => void;
}

const PlaceholderInput: React.FC<PlaceholderInputProps> = ({ onAdd }) => {
  const [value, setValue] = useState('');

  const handleKeyDown = (key: string) => {
    if (key === 'Enter' && value.trim()) {
      onAdd(value.trim());
      setValue('');
    }
  };

  return (
    <Input
      value={value}
      onChange={({ detail }) => setValue(detail.value)}
      onKeyDown={({ detail }) => handleKeyDown(detail.key)}
      placeholder="Type and press Enter to add"
      ariaLabel="Add placeholder"
    />
  );
};

// ─── Sub-component: Fixed Params Editor ────────────────────────────────

interface FixedParamsEditorProps {
  fixedParams: Record<string, string>;
  onChange: (updated: Record<string, string>) => void;
}

const FixedParamsEditor: React.FC<FixedParamsEditorProps> = ({
  fixedParams,
  onChange,
}) => {
  const entries = Object.entries(fixedParams);

  const handleValueChange = (key: string, value: string) => {
    onChange({ ...fixedParams, [key]: value });
  };

  return (
    <SpaceBetween size="xs">
      {entries.map(([key, value]) => (
        <ColumnLayout key={key} columns={2}>
          <Box fontWeight="bold" fontSize="body-s">
            {key}
          </Box>
          <Input
            value={value}
            onChange={({ detail }) => handleValueChange(key, detail.value)}
            ariaLabel={`Fixed param ${key}`}
          />
        </ColumnLayout>
      ))}
      {entries.length === 0 && (
        <Box color="text-status-inactive" fontSize="body-s">
          No fixed parameters
        </Box>
      )}
    </SpaceBetween>
  );
};

// ─── Sub-component: Comparison Parameters Editor ───────────────────────

interface ComparisonParamsEditorProps {
  toolName: string;
  stableKey: string;
  comparison: NonNullable<TemplateDefinition['parameters']['comparison']>;
  requiredParams: string[];
  parameters: TemplateDefinition['parameters'];
  dispatch: React.Dispatch<ConfigPageAction>;
}

const ComparisonParamsEditor: React.FC<ComparisonParamsEditorProps> = ({
  toolName,
  stableKey,
  comparison,
  requiredParams,
  parameters,
  dispatch,
}) => {
  const handleComparisonFieldChange = (field: string, value: unknown) => {
    const updatedComparison = { ...comparison, [field]: value };
    dispatch({
      type: 'UPDATE_TEMPLATE',
      toolName,
      stableKey,
      field: 'parameters',
      value: {
        ...parameters,
        required: requiredParams,
        comparison: updatedComparison,
      },
    });
  };

  return (
    <Container header={<Header variant="h3">Comparison Parameters</Header>}>
      <ColumnLayout columns={2}>
        <FormField label="Comparison parameter" description="The parameter being compared">
          <Input
            value={comparison.param}
            onChange={({ detail }) =>
              handleComparisonFieldChange('param', detail.value)
            }
            ariaLabel="Comparison parameter name"
          />
        </FormField>

        <FormField label="Base value" description="The baseline value for comparison (leave empty for symmetric comparisons)">
          <Input
            value={comparison.base_value}
            onChange={({ detail }) =>
              handleComparisonFieldChange('base_value', detail.value)
            }
            ariaLabel="Comparison base value"
          />
        </FormField>

        <FormField label="Placeholders" description="Template placeholders for comparison values">
          <SpaceBetween size="xs">
            <TokenGroup
              items={comparison.placeholders.map((p) => ({
                label: p,
                dismissLabel: `Remove ${p}`,
              }))}
              onDismiss={({ detail }) => {
                const updated = comparison.placeholders.filter(
                  (_, i) => i !== detail.itemIndex,
                );
                handleComparisonFieldChange('placeholders', updated);
              }}
            />
            <PlaceholderInput
              onAdd={(value) =>
                handleComparisonFieldChange('placeholders', [
                  ...comparison.placeholders,
                  value,
                ])
              }
            />
          </SpaceBetween>
        </FormField>

        <FormField label="Fixed parameters" description="Parameters held constant during comparison">
          <FixedParamsEditor
            fixedParams={comparison.fixed_params ?? {}}
            onChange={(updated) =>
              handleComparisonFieldChange('fixed_params', updated)
            }
          />
        </FormField>
      </ColumnLayout>
    </Container>
  );
};

// ─── Sub-component: Implicit Parameters Editor ─────────────────────────

interface ImplicitParamsEditorProps {
  toolName: string;
  stableKey: string;
  implicit: Record<string, string>;
  requiredParams: string[];
  parameters: TemplateDefinition['parameters'];
  dispatch: React.Dispatch<ConfigPageAction>;
}

const ImplicitParamsEditor: React.FC<ImplicitParamsEditorProps> = ({
  toolName,
  stableKey,
  implicit,
  requiredParams,
  parameters,
  dispatch,
}) => {
  const entries = Object.entries(implicit);

  const handleImplicitChange = (key: string, value: string) => {
    const updated = { ...implicit, [key]: value };
    dispatch({
      type: 'UPDATE_TEMPLATE',
      toolName,
      stableKey,
      field: 'parameters',
      value: {
        ...parameters,
        required: requiredParams,
        implicit: updated,
      },
    });
  };

  return (
    <Container header={<Header variant="h3">Implicit Parameters</Header>}>
      <SpaceBetween size="s">
        {entries.map(([key, value]) => (
          <FormField key={key} label={key}>
            <Input
              value={value}
              onChange={({ detail }) => handleImplicitChange(key, detail.value)}
              ariaLabel={`Implicit parameter ${key}`}
            />
          </FormField>
        ))}
        {entries.length === 0 && (
          <Box color="text-status-inactive">No implicit parameters</Box>
        )}
      </SpaceBetween>
    </Container>
  );
};

// ─── Sub-component: Template Card ──────────────────────────────────────

interface TemplateCardProps {
  toolName: string;
  template: TemplateDefinition & { enabled: boolean };
  dispatch: React.Dispatch<ConfigPageAction>;
  validationErrors: ValidationErrors;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  toolName,
  template,
  dispatch,
  validationErrors,
}) => {
  const weightErrorKey = `templates.${toolName}.${template._stableKey}.weight`;
  const idErrorKey = `templates.${toolName}.${template._stableKey}.id`;

  const handleToggle = () => {
    dispatch({
      type: 'TOGGLE_TEMPLATE',
      toolName,
      stableKey: template._stableKey!,
    });
  };

  const handleFieldUpdate = (field: string, value: unknown) => {
    dispatch({
      type: 'UPDATE_TEMPLATE',
      toolName,
      stableKey: template._stableKey!,
      field,
      value,
    });
  };

  const handleDuplicate = () => {
    dispatch({
      type: 'DUPLICATE_TEMPLATE',
      toolName,
      stableKey: template._stableKey!,
    });
  };

  const handleRemove = () => {
    dispatch({
      type: 'REMOVE_TEMPLATE',
      toolName,
      stableKey: template._stableKey!,
    });
  };

  const handleWeightChange = (rawValue: string) => {
    const parsed = parseFloat(rawValue);
    handleFieldUpdate('weight', isNaN(parsed) ? rawValue : parsed);
  };

  return (
    <Container
      header={
        <Header
          variant="h3"
          actions={
            <SpaceBetween size="xs" direction="horizontal">
              <Button onClick={handleDuplicate} iconName="copy" variant="icon">
                Duplicate
              </Button>
              <Button onClick={handleRemove} iconName="remove" variant="icon">
                Remove
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Toggle
              checked={template.enabled}
              onChange={handleToggle}
              ariaLabel={`Enable template ${template.id}`}
            />
            <span>{template.id}</span>
            {template.isComparison && <Badge color="blue">Comparison</Badge>}
          </SpaceBetween>
        </Header>
      }
    >
      <SpaceBetween size="m">
        <ColumnLayout columns={2}>
          <FormField label="Template ID" errorText={validationErrors[idErrorKey]}>
            <Input
              value={template.id}
              onChange={({ detail }) => handleFieldUpdate('id', detail.value)}
              ariaLabel="Template ID"
            />
          </FormField>

          <FormField
            label="Weight"
            description="Sampling probability for this template"
            errorText={validationErrors[weightErrorKey]}
            constraintText={`Must be between ${WEIGHT_MIN} and ${WEIGHT_MAX}`}
          >
            <Input
              value={String(template.weight)}
              onChange={({ detail }) => handleWeightChange(detail.value)}
              type="number"
              inputMode="decimal"
              ariaLabel="Template weight"
            />
          </FormField>
        </ColumnLayout>

        <FormField label="Template string" description="Question pattern with {placeholder} syntax">
          <Textarea
            value={template.template}
            onChange={({ detail }) => handleFieldUpdate('template', detail.value)}
            rows={TEMPLATE_STRING_ROWS}
            ariaLabel="Template string"
          />
        </FormField>

        <FormField label="Description">
          <Input
            value={template.description}
            onChange={({ detail }) =>
              handleFieldUpdate('description', detail.value)
            }
            ariaLabel="Template description"
          />
        </FormField>

        {/* Comparison parameters editor */}
        {template.parameters.comparison && (
          <ComparisonParamsEditor
            toolName={toolName}
            stableKey={template._stableKey!}
            comparison={template.parameters.comparison}
            requiredParams={template.parameters.required}
            parameters={template.parameters}
            dispatch={dispatch}
          />
        )}

        {/* Implicit parameters editor */}
        {template.parameters.implicit &&
          Object.keys(template.parameters.implicit).length > 0 && (
            <ImplicitParamsEditor
              toolName={toolName}
              stableKey={template._stableKey!}
              implicit={template.parameters.implicit}
              requiredParams={template.parameters.required}
              parameters={template.parameters}
              dispatch={dispatch}
            />
          )}
      </SpaceBetween>
    </Container>
  );
};

// ─── Main Component ────────────────────────────────────────────────────

const TemplatesSection: React.FC<TemplatesSectionProps> = ({
  state,
  dispatch,
  validationErrors,
}) => {
  const toolEntries = useMemo(
    () => Object.entries(state.templates),
    [state.templates],
  );

  // Compute duplicate IDs across all tools for display
  const duplicateWarnings = useMemo(() => {
    const warnings: Record<string, string[]> = {};
    for (const [toolName, templates] of toolEntries) {
      const enabledTemplates = templates.filter((t) => t.enabled);
      const dupes = validateUniqueIds(enabledTemplates);
      if (dupes.length > 0) {
        warnings[toolName] = dupes;
      }
    }
    return warnings;
  }, [toolEntries]);

  const handleAddCustomTemplate = (toolName: string) => {
    dispatch({
      type: 'ADD_TEMPLATE',
      toolName,
      template: createEmptyTemplate(),
    });
  };

  if (toolEntries.length === 0) {
    return (
      <ExpandableSection
        headerText="Templates"
        variant="container"
        defaultExpanded
      >
        <Box color="text-status-inactive" padding={{ vertical: 's' }}>
          No templates available. Templates will appear after pre-population.
        </Box>
      </ExpandableSection>
    );
  }

  return (
    <ExpandableSection
      headerText="Templates"
      variant="container"
      defaultExpanded
    >
      <SpaceBetween size="l">
        {toolEntries.map(([toolName, templates]) => {
          const enabledCount = templates.filter((t) => t.enabled).length;
          const totalCount = templates.length;
          const hasDuplicates = toolName in duplicateWarnings;

          return (
            <SpaceBetween key={toolName} size="m">
              <Header
                variant="h2"
                counter={`(${enabledCount}/${totalCount} enabled)`}
                actions={
                  <Button
                    onClick={() => handleAddCustomTemplate(toolName)}
                    iconName="add-plus"
                  >
                    Add Custom Template
                  </Button>
                }
              >
                {toolName}
              </Header>

              {hasDuplicates && (
                <Alert type="warning">
                  Duplicate template IDs detected:{' '}
                  {duplicateWarnings[toolName].join(', ')}. Each template ID
                  must be unique within a tool group.
                </Alert>
              )}

              {templates.map((template) => (
                <TemplateCard
                  key={template._stableKey ?? template.id}
                  toolName={toolName}
                  template={template}
                  dispatch={dispatch}
                  validationErrors={validationErrors}
                />
              ))}
            </SpaceBetween>
          );
        })}
      </SpaceBetween>
    </ExpandableSection>
  );
};

export default TemplatesSection;
